import type { ClassId } from '../constants.ts';
import type { Vec3 } from '../math.ts';
import type { PlayerState } from '../player.ts';
import { centreOf, colOf, isOpen, rowOf } from './nav.ts';
import { threatCell, type InfluenceMap } from './influence.ts';

/**
 * How much danger each class will accept for a given prize, as a curve.
 *
 * This is the piece a priority ladder could not express. A rung either fires or
 * it does not, so a laddered bot is aggressive or paralysed with nothing in
 * between — which is exactly what the Mentalists and Arcanists were doing. A
 * curve has a middle, and the middle is where probing lives.
 *
 * Each class reads the same map and asks a different question of it:
 *
 *   Cleric     falls off a cliff almost at once. Any threat is disqualifying,
 *              so it hugs ground its own side holds and follows the team rather
 *              than choosing its own.
 *   Magician   tolerates danger *while standing on something*, and very little
 *              otherwise: willing to defend a pool, unwilling to go looking for
 *              a fight. The kink is the whole personality.
 *   Mentalist  wants the contour where support and threat cancel — not the
 *              safest ground and not the most dangerous, but the line between
 *              them. That is what probing is, written down.
 *   Arcanist   flat, then a cliff. Indifferent to one enemy and sharply averse
 *              to three, which is "declines even fights" as a shape rather than
 *              an if-statement. It takes a duel anywhere on the map and refuses
 *              a crowd anywhere on the map.
 */
export function riskPenalty(cls: ClassId, threat: number, onValue: boolean): number {
  switch (cls) {
    case 'cleric':
      // Steep from zero. Two mages' worth of threat is already unbearable.
      return threat * threat * 9 + threat * 6;

    case 'magician':
      // Standing on the objective is worth taking hits for; wandering is not.
      return onValue ? threat * 2.5 : threat * threat * 5 + threat * 4;

    case 'mentalist':
      // Cheap up to about one enemy, expensive past it: sits on the line.
      return threat < 0.9 ? threat * 1.2 : 1.1 + (threat - 0.9) ** 2 * 12;

    case 'arcanist':
    default:
      // Flat, then a cliff. One is an opportunity; three is a funeral.
      return threat < 1.3 ? threat * 0.5 : 0.65 + (threat - 1.3) ** 2 * 16;
  }
}

/**
 * How much a class likes having its own side nearby.
 *
 * Separate from the risk curve because they are different feelings. A Cleric
 * wants to be *among* people — that is where the work is — and an Arcanist would
 * genuinely rather be alone, since a crowd is what turns its opportunity into
 * somebody else's kill.
 */
function companyWeight(cls: ClassId): number {
  switch (cls) {
    case 'cleric': return 2.2;
    case 'magician': return 0.9;
    case 'mentalist': return 1.4;
    case 'arcanist': default: return 0.15;
  }
}

/** How much further a bot will walk for the same prize, per metre. */
const WALK_COST = 0.02;

/**
 * What a crowd costs, on top of what company is worth.
 *
 * Company is linear and crowding is quadratic, so a second friendly is a comfort
 * and a fourth is a Fire Ball waiting to happen. Clerics want to be *among*
 * people and would otherwise pile onto the same square as each other — this is
 * the same anti-clustering the movement nudge does, but decided rather than
 * shoved, which is why it does not fight the path and cannot produce a dance.
 */
const CROWD_COST = 1.6;

/**
 * How much of a threat each nearby friendly cancels out.
 *
 * The risk curves used to read *raw* threat, which meant a bot was as frightened
 * of two enemies with its whole team beside it as it was alone. That is why a
 * side winning the match would stop pushing: every objective still worth taking
 * was defended, every defended objective looked lethal, and nobody could see
 * that arriving together changes the answer.
 *
 * Nearly one for one, so three attacking two is comfortable and two attacking
 * three is not. Separate from `companyWeight`, which is whether a class *likes*
 * company — an Arcanist would rather be alone and is still safer in a crowd, and
 * those are different facts about the same mage.
 */
const RELIEF = 0.8;

/**
 * The best square this mage can think of standing on.
 *
 * One sweep of the grid, scoring every open cell it can reach the same way:
 * what is it worth, who is with me, what is waiting there, and how far is it.
 * The class differences are entirely in the two functions above, which is the
 * simplification the per-class ladders never managed — four doctrines collapse
 * to four curves over one search.
 *
 * `radius` bounds the sweep. A Cleric has no business scoring the far side of
 * the arena, and an Arcanist very much does.
 */
export function bestCell(
  map: InfluenceMap,
  self: PlayerState,
  radius: number,
  bonus?: (at: Vec3) => number,
  anchor?: Vec3,
): Vec3 | null {
  const { nav } = map;
  const value = map.value[self.faction]!;
  const mine = map.presence[self.faction]!;
  const company = companyWeight(self.cls);

  let best: Vec3 | null = null;
  let bestScore = -Infinity;

  /*
   * Only the window the radius can reach.
   *
   * Sweeping all six thousand cells and rejecting most of them cost about three
   * milliseconds a frame across a full match — a fifth of the frame budget spent
   * measuring the distance to squares that were never candidates. The bound is
   * already known; it may as well be applied to the loop rather than inside it.
   */
  const hub = anchor ?? self.pos;
  const lo = { c: colOf(nav, hub.x - radius), r: rowOf(nav, hub.z - radius) };
  const hi = { c: colOf(nav, hub.x + radius), r: rowOf(nav, hub.z + radius) };

  for (let r = Math.max(0, lo.r); r <= Math.min(nav.rows - 1, hi.r); r++) {
    for (let c = Math.max(0, lo.c); c <= Math.min(nav.cols - 1, hi.c); c++) {
      if (!isOpen(nav, c, r)) continue;
      const at = centreOf(nav, c, r);
      const away = Math.hypot(at.x - self.pos.x, at.z - self.pos.z);
      /*
       * The search is bounded around the *anchor*, not the searcher.
       *
       * Centred on the bot, a bound is only ever a leash on how far it will step
       * — so a Cleric that drifts thirty metres from its shrine finds a new
       * thirty-metre circle around wherever it has ended up, and never comes
       * back. Anchored to the shrine, the same number means "a Cleric considers
       * ground near home", which is what it was supposed to mean.
       */
      if (Math.hypot(at.x - hub.x, at.z - hub.z) > radius) continue;

      const i = r * nav.cols + c;
      const worth = value[i]!;
      const threat = threatCell(map, self.faction, i);

      // Support is discounted by the mage's own contribution, or a bot standing
      // alone reads its own presence as company and never goes to find any.
      const friends = Math.max(0, mine[i]! - (away < nav.cell ? 1 : 0));

      // What is left of the danger once the people beside you are counted.
      const pressure = Math.max(0, threat - friends * RELIEF);

      const score = worth
        + friends * company - friends * friends * CROWD_COST
        - riskPenalty(self.cls, pressure, worth > 0.4)
        - away * WALK_COST
        + (bonus ? bonus(at) : 0);

      if (score <= bestScore) continue;
      bestScore = score;
      best = at;
    }
  }
  return best;
}
